import type { StopListSettingsModel } from '@/shared/generated'

import React, { Fragment } from 'react'
import { isNumber, pipe } from 'lodash/fp'
import { CheckIcon, XIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useSettingsSchemasQuery } from '@/entities/core'

import {
	Item,
	ItemContent,
	ItemDescription,
	ItemMedia,
	ItemTitle,
} from '@/shared/components/ui/item'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
	getResolvedSchema,
	translateSchemaKeys,
} from '@/shared/tools/json-schema-utils'

export function StopListDefaultRulesView({
	settings,
	compact = false,
}: {
	settings?: StopListSettingsModel
	compact?: boolean
}) {
	const { t } = useTranslation()

	const schemaQuery = useSettingsSchemasQuery({
		select: pipe([
			getResolvedSchema('part:stop_list'),
			(schema) => translateSchemaKeys(schema, t),
		]),
	})

	return (
		<div className='flex flex-col gap-2'>
			{schemaQuery.isLoading ? (
				<>
					<Skeleton className='h-8 max-w-56 animate-pulse rounded-lg' />
					<Skeleton className='h-8 max-w-96 animate-pulse rounded-lg' />
					<Skeleton className='h-8 max-w-96 animate-pulse rounded-lg' />
				</>
			) : (
				Object.entries(
					(schemaQuery.data?.properties.default_rules.properties ||
						{}) as Record<string, { measure?: string }>
				)?.map(([rule, schema]) => {
					const ruleValue = settings?.default_rules?.[rule]

					return (
						<Item
							className={compact ? 'px-2 py-1' : ''}
							size='sm'
							variant='muted'
							key={rule}
						>
							<ItemMedia>
								{ruleValue ? (
									<CheckIcon
										className='size-4 shrink-0 rounded-lg bg-green-400 p-0.5 text-white dark:bg-green-800'
										strokeWidth={3}
									/>
								) : (
									<XIcon
										className='size-4 shrink-0 rounded-lg bg-red-400 p-0.5 text-white dark:bg-red-800'
										strokeWidth={3}
									/>
								)}
							</ItemMedia>
							<ItemContent className='flex-row gap-2'>
								<ItemTitle
									className={
										ruleValue
											? 'font-semibold'
											: 'font-semibold text-muted-foreground'
									}
								>
									{t(`og_stoplist_default_rules_${rule}`)}
								</ItemTitle>
								<ItemDescription>
									{isNumber(ruleValue) && (
										<Fragment>
											<span className='mr-1 text-sm font-bold'>
												{ruleValue}
											</span>
											<span className='text-sm'>
												{!!schema.measure && !!ruleValue && t(schema.measure)}
											</span>
										</Fragment>
									)}
								</ItemDescription>
							</ItemContent>
						</Item>
					)
				})
			)}
		</div>
	)
}
